import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { getServicesManager } from '../../../services'
import { RegisteredTwilioNumber, OptionalTwilioNumber } from '../../../interfaces/db'

export interface PhoneNumbersState {
  phoneNumbers: RegisteredTwilioNumber[]
  availableNumbers: OptionalTwilioNumber[]
  isLoading: boolean
  isAcquiring: boolean
  isReleasing: boolean
  error: string | null
}

const initialState: PhoneNumbersState = {
  phoneNumbers: [],
  availableNumbers: [],
  isLoading: false,
  isAcquiring: false,
  isReleasing: false,
  error: null
}

// Async thunks
export const fetchPhoneNumbers = createAsyncThunk(
  'phoneNumbers/fetchPhoneNumbers',
  async (_, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      const numbers = await servicesManager.getTwilioNumberForTeam()
      if (!numbers) {
        throw new Error('Failed to fetch phone numbers')
      }
      return numbers
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error')
    }
  }
)

export const fetchAvailableNumbers = createAsyncThunk(
  'phoneNumbers/fetchAvailableNumbers',
  async (options: {
    area_code?: string
    iso_country_code?: string
    state_or_province?: string
    substring?: string
    number_type?: 'Local' | 'Mobile' | 'TollFree'
  }, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      const numbers = await servicesManager.getOptionsForTwilioNumber(options)
      if (!numbers) {
        throw new Error('Failed to fetch available numbers')
      }
      return numbers
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error')
    }
  }
)

export const acquirePhoneNumber = createAsyncThunk(
  'phoneNumbers/acquirePhoneNumber',
  async ({ number, countryIsoCode, numberType }: {
    number: OptionalTwilioNumber
    countryIsoCode?: string
    numberType?: 'Local' | 'Mobile' | 'TollFree'
  }, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      const result = await servicesManager.registerTwilioNumberForTeam(number.phone_number, countryIsoCode, numberType)
      if (typeof result === 'string') {
        throw new Error(result)
      }
      return result
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error')
    }
  }
)

export const releasePhoneNumber = createAsyncThunk(
  'phoneNumbers/releasePhoneNumber',
  async (repPhoneValue: string, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      const result = await servicesManager.releaseTwilioNumberForTeam(repPhoneValue)
      
      if (!result) {
        throw new Error('Failed to release phone number')
      }
      
      return repPhoneValue
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error')
    }
  }
)

const phoneNumbersSlice = createSlice({
  name: 'phoneNumbers',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearAvailableNumbers: (state) => {
      state.availableNumbers = []
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch phone numbers
      .addCase(fetchPhoneNumbers.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchPhoneNumbers.fulfilled, (state, action) => {
        state.isLoading = false
        state.phoneNumbers = action.payload
      })
      .addCase(fetchPhoneNumbers.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      
      // Fetch available numbers
      .addCase(fetchAvailableNumbers.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchAvailableNumbers.fulfilled, (state, action) => {
        state.isLoading = false
        state.availableNumbers = action.payload
      })
      .addCase(fetchAvailableNumbers.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      
      // Acquire phone number
      .addCase(acquirePhoneNumber.pending, (state) => {
        state.isAcquiring = true
        state.error = null
      })
      .addCase(acquirePhoneNumber.fulfilled, (state, action) => {
        state.isAcquiring = false
        // Add the newly acquired number to the store
        state.phoneNumbers.push(action.payload)
      })
      .addCase(acquirePhoneNumber.rejected, (state, action) => {
        state.isAcquiring = false
        state.error = action.payload as string
      })
      
      // Release phone number
      .addCase(releasePhoneNumber.pending, (state) => {
        state.isReleasing = true
        state.error = null
      })
      .addCase(releasePhoneNumber.fulfilled, (state, action) => {
        state.isReleasing = false
        // Remove the released number from the store
        state.phoneNumbers = state.phoneNumbers.filter(
          number => number.rep_phone_value !== action.payload
        )
      })
      .addCase(releasePhoneNumber.rejected, (state, action) => {
        state.isReleasing = false
        state.error = action.payload as string
      })
  }
})

export const { clearError, clearAvailableNumbers } = phoneNumbersSlice.actions
export default phoneNumbersSlice.reducer 